import { Injectable, Injector } from '@angular/core';
import { DialogService } from '@ds/ng-wt-dialog';

/**
 * DialogCleanupService - Comprehensive cleanup of all dialog types
 * 
 * Handles closing:
 * - DialogService dialogs (custom DS dialogs)
 * - Angular Material dialogs (MatDialog)
 * - CDK overlays
 * - HotToast containers
 * - Backdrop elements
 * 
 * CRITICAL: Called during logout to ensure no orphaned UI elements remain
 */
@Injectable({
  providedIn: 'root'
})
export class DialogCleanupService {

  constructor(private injector: Injector) { }

  /**
   * Close ALL open dialogs of all types
   * Call this during logout to prevent UI artifacts
   * Safe to call multiple times
   */
  public closeAllDialogs(): void {
    console.log('[DialogCleanup] Starting comprehensive dialog cleanup');
    
    // 1. Close DialogService dialogs (DS library)
    this.closeDialogServiceDialogs();
    
    // 2. Close Angular Material dialogs
    this.closeMatDialogs();
    
    // 3. Dispose CDK overlays
    this.disposeCdkOverlays();
    
    // 4. Close HotToast containers
    this.closeHotToastContainers();
    
    // 5. Remove backdrop elements
    this.removeBackdropElements();
    
    // 6. Clear any floating elements
    this.clearFloatingElements();
    
    console.log('[DialogCleanup] Dialog cleanup completed');
  }

  /**
   * Close all DialogService (DS) dialogs
   * These are the custom dialogs used throughout the app
   */
  private closeDialogServiceDialogs(): void {
    try {
      const dsDialog = this.injector.get(DialogService);
      if (dsDialog && dsDialog.dialogs && dsDialog.dialogs.length > 0) {
        console.log(`[DialogCleanup] Closing ${dsDialog.dialogs.length} DialogService dialogs`);
        
        // Create a copy to avoid modification during iteration
        const dialogs = [...dsDialog.dialogs];
        dialogs.forEach((dialog, index) => {
          try {
            if (dialog && typeof dialog.close === 'function') {
              dialog.close();
              console.log(`[DialogCleanup] Closed DS dialog ${index}`);
            }
          } catch (error) {
            console.warn(`[DialogCleanup] Error closing DS dialog ${index}:`, error);
          }
        });
      }
    } catch (error) {
      console.warn('[DialogCleanup] Error accessing DialogService:', error);
    }
  }

  /**
   * Close all Angular Material dialogs
   */
  private closeMatDialogs(): void {
    try {
      // Safely get MatDialog if it exists
      const matDialogProvider = this.tryGetMatDialog();
      if (matDialogProvider && matDialogProvider.openDialogs && matDialogProvider.openDialogs.length > 0) {
        console.log(`[DialogCleanup] Closing ${matDialogProvider.openDialogs.length} Material dialogs`);
        
        // Create a copy to avoid modification during iteration
        const dialogs = [...matDialogProvider.openDialogs];
        dialogs.forEach((dialog, index) => {
          try {
            if (dialog && typeof dialog.close === 'function') {
              dialog.close();
              console.log(`[DialogCleanup] Closed Material dialog ${index}`);
            }
          } catch (error) {
            console.warn(`[DialogCleanup] Error closing Material dialog ${index}:`, error);
          }
        });
      }
    } catch (error) {
      console.warn('[DialogCleanup] Error accessing MatDialog:', error);
    }
  }

  /**
   * Safely get MatDialog if it exists
   * Returns null if MatDialog is not available
   */
  private tryGetMatDialog(): any {
    try {
      // Try to get MatDialog from injector
      return this.injector.get('MatDialog' as any, null);
    } catch {
      return null;
    }
  }

  /**
   * Dispose CDK overlays
   */
  private disposeCdkOverlays(): void {
    try {
      // Get the Overlay service
      const overlayProvider = this.tryGetOverlay();
      if (overlayProvider && overlayProvider.dispose && typeof overlayProvider.dispose === 'function') {
        console.log('[DialogCleanup] Disposing CDK overlays');
        overlayProvider.dispose();
      }
    } catch (error) {
      console.warn('[DialogCleanup] Error disposing CDK overlays:', error);
    }
  }

  /**
   * Safely get CDK Overlay if it exists
   */
  private tryGetOverlay(): any {
    try {
      return this.injector.get('Overlay' as any, null);
    } catch {
      return null;
    }
  }

  /**
   * Close HotToast containers
   */
  private closeHotToastContainers(): void {
    try {
      // Try to access HotToastService if available
      const hotToastProvider = this.tryGetHotToast();
      if (hotToastProvider) {
        // HotToast typically has a close() method
        if (hotToastProvider.close && typeof hotToastProvider.close === 'function') {
          hotToastProvider.close();
          console.log('[DialogCleanup] Closed HotToast containers');
        }
        
        // Alternative: close all active toasts if available
        if (hotToastProvider.hot$ && hotToastProvider.hot$.subscribe) {
          console.log('[DialogCleanup] HotToast service found and cleaned');
        }
      }
    } catch (error) {
      console.warn('[DialogCleanup] Error handling HotToast:', error);
    }
  }

  /**
   * Safely get HotToastService if it exists
   */
  private tryGetHotToast(): any {
    try {
      return this.injector.get('HotToastService' as any, null);
    } catch {
      return null;
    }
  }

  /**
   * Remove all backdrop elements from DOM
   * These can be orphaned if dialog cleanup is incomplete
   */
  private removeBackdropElements(): void {
    try {
      // Find all Material backdrop elements
      const backdrops = document.querySelectorAll('.cdk-overlay-backdrop, .mat-dialog-container, .cdk-overlay-pane');
      if (backdrops && backdrops.length > 0) {
        console.log(`[DialogCleanup] Removing ${backdrops.length} backdrop elements`);
        
        backdrops.forEach((backdrop, index) => {
          try {
            if (backdrop && backdrop.parentNode) {
              backdrop.parentNode.removeChild(backdrop);
              console.log(`[DialogCleanup] Removed backdrop element ${index}`);
            }
          } catch (error) {
            console.warn(`[DialogCleanup] Error removing backdrop ${index}:`, error);
          }
        });
      }

      // Remove modal backdrops
      const modalBackdrops = document.querySelectorAll('.modal-backdrop, .cdk-dialog-container');
      if (modalBackdrops && modalBackdrops.length > 0) {
        console.log(`[DialogCleanup] Removing ${modalBackdrops.length} modal backdrops`);
        
        modalBackdrops.forEach((backdrop, index) => {
          try {
            if (backdrop && backdrop.parentNode) {
              backdrop.parentNode.removeChild(backdrop);
            }
          } catch (error) {
            console.warn(`[DialogCleanup] Error removing modal backdrop ${index}:`, error);
          }
        });
      }
    } catch (error) {
      console.warn('[DialogCleanup] Error removing backdrop elements:', error);
    }
  }

  /**
   * Clear any floating dialog/toast elements from the DOM
   */
  private clearFloatingElements(): void {
    try {
      // DS Flyout dialogs
      const dsDialogs = document.querySelectorAll('[class*="ds-flyout"], [class*="ds-dialog"]');
      dsDialogs.forEach((element) => {
        try {
          if (element && element.parentNode) {
            element.parentNode.removeChild(element);
          }
        } catch (error) {
          console.warn('[DialogCleanup] Error removing DS dialog element:', error);
        }
      });

      // Material dialog containers
      const matDialogs = document.querySelectorAll('mat-dialog-container');
      matDialogs.forEach((element) => {
        try {
          if (element && element.parentNode) {
            element.parentNode.removeChild(element);
          }
        } catch (error) {
          console.warn('[DialogCleanup] Error removing Material dialog element:', error);
        }
      });

      // CDK Overlay panes
      const overlayPanes = document.querySelectorAll('.cdk-overlay-pane');
      overlayPanes.forEach((element) => {
        try {
          if (element && element.parentNode) {
            element.parentNode.removeChild(element);
          }
        } catch (error) {
          console.warn('[DialogCleanup] Error removing overlay pane:', error);
        }
      });

      console.log('[DialogCleanup] Floating elements cleared');
    } catch (error) {
      console.warn('[DialogCleanup] Error clearing floating elements:', error);
    }
  }
}
